import { useEffect, useState } from "react"
import { Modal, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import Settings from "@/common/Settings"
import { APIService } from "@/common/API"

interface FileRecord {
  id: string
  filePath: string
}

interface Props {
  files: FileRecord[]
  canDelete?: boolean
  onDeleted?: (id: string) => void
}

const fileUrl = (filePath: string) => `${Settings.BasePath}files/${filePath}`

const IncidentFileGallery = ({ files, canDelete = false, onDeleted }: Props) => {
  const { t } = useTranslation("global")
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [lightbox, setLightbox] = useState<number | null>(null)

  const open = (i: number) => setLightbox(i)
  const close = () => setLightbox(null)
  const prev = () => setLightbox(i => (i !== null && i > 0 ? i - 1 : files.length - 1))
  const next = () => setLightbox(i => (i !== null && i < files.length - 1 ? i + 1 : 0))

  useEffect(() => {
    if (lightbox === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
      if (e.key === "ArrowLeft") prev()
      if (e.key === "ArrowRight") next()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [lightbox])

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("¿Eliminar este archivo?"))) return
    setDeletingId(id)
    try {
      await APIService.getAction("Incident/Files/Delete", `id=${id}`)
      onDeleted?.(id)
      if (lightbox !== null && files.length <= 1) close()
      else if (lightbox !== null && lightbox >= files.length - 1) setLightbox(lightbox - 1)
    } finally {
      setDeletingId(null)
    }
  }

  if (files.length === 0) return null

  const current = lightbox !== null ? files[lightbox] : null

  return (
    <>
      <div className="d-flex flex-wrap gap-2 mt-2">
        {files.map((f, i) => (
          <div
            key={f.id}
            className="position-relative"
            style={{ width: 90, height: 90, flexShrink: 0 }}
          >
            <img
              src={fileUrl(f.filePath)}
              alt=""
              onClick={() => open(i)}
              style={{
                width: 90,
                height: 90,
                objectFit: "cover",
                borderRadius: 6,
                cursor: "pointer",
                border: "1px solid #dee2e6",
                transition: "opacity .15s",
              }}
              onMouseEnter={e => (e.currentTarget.style.opacity = "0.8")}
              onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
            />
            {canDelete && (
              <button
                className="btn btn-danger btn-sm position-absolute d-flex align-items-center justify-content-center p-0"
                style={{ top: 3, right: 3, width: 20, height: 20, fontSize: 12, lineHeight: 1 }}
                onClick={() => handleDelete(f.id)}
                disabled={deletingId === f.id}
                title={t("Delete")}
              >
                {deletingId === f.id
                  ? <Spinner animation="border" size="sm" style={{ width: 10, height: 10 }} />
                  : "×"}
              </button>
            )}
          </div>
        ))}
      </div>

      {current && (
        <Modal show centered size="lg" onHide={close} contentClassName="bg-dark border-0">
          <Modal.Body className="p-0 position-relative text-center" style={{ minHeight: 300 }}>
            <img
              src={fileUrl(current.filePath)}
              alt=""
              style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain", borderRadius: 4 }}
            />

            {/* counter */}
            <div
              className="position-absolute top-0 start-0 m-2 badge bg-dark bg-opacity-75 text-white"
              style={{ fontSize: 12 }}
            >
              {(lightbox ?? 0) + 1} / {files.length}
            </div>

            {/* close */}
            <button
              className="btn btn-sm btn-dark position-absolute top-0 end-0 m-2"
              onClick={close}
              title={t("Close")}
            >
              ✕
            </button>

            {/* prev */}
            {files.length > 1 && (
              <button
                className="btn btn-dark btn-sm position-absolute top-50 start-0 translate-middle-y ms-2"
                onClick={prev}
                style={{ opacity: 0.8 }}
              >
                ‹
              </button>
            )}

            {/* next */}
            {files.length > 1 && (
              <button
                className="btn btn-dark btn-sm position-absolute top-50 end-0 translate-middle-y me-2"
                onClick={next}
                style={{ opacity: 0.8 }}
              >
                ›
              </button>
            )}
          </Modal.Body>

          <Modal.Footer className="bg-dark border-top border-secondary d-flex justify-content-between py-2">
            <a
              href={fileUrl(current.filePath)}
              target="_blank"
              rel="noreferrer"
              className="btn btn-sm btn-outline-light"
            >
              <i className="fas fa-external-link-alt me-1" />{t("Ver original")}
            </a>
            {canDelete && (
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => handleDelete(current.id)}
                disabled={deletingId === current.id}
              >
                {deletingId === current.id
                  ? <Spinner animation="border" size="sm" />
                  : <><i className="fas fa-trash me-1" />{t("Eliminar")}</>}
              </button>
            )}
          </Modal.Footer>
        </Modal>
      )}
    </>
  )
}

export default IncidentFileGallery
